/**
 * extractBizLogicService.js
 *
 * Client-side service for the Extract Business Logic API.
 *
 * Endpoints:
 *   POST /extractbizlogic          → queue an EBL job, returns { job_id, status }
 *   GET  /extractbizlogic/:jobId/status  → poll job status
 *   GET  /extractbizlogic/:jobId/results → fetch full artifacts
 */

import { getAPIBaseUrl } from '../environment'

const BASE = () => `${getAPIBaseUrl()}/extractbizlogic`

/**
 * Start EBL extraction for a Code Analysis job.
 *
 * @param {object} params
 * @param {string} params.scoutAccountId
 * @param {string} params.applicationName
 * @param {string} params.userId
 * @param {string} params.caJobId             - Code Analysis job ID
 * @param {string} [params.scope]             - 'all' | 'selected'
 * @param {boolean} [params.includeRules]
 * @param {boolean} [params.includeTxGroups]
 * @param {boolean} [params.includeGrounding]
 * @returns {Promise<{ success, job_id, status, message, error }>}
 */
export async function startExtractBizLogic(params) {
  const {
    scoutAccountId,
    applicationName,
    userId,
    caJobId,
    scope = 'all',
    includeSpec = true,
    includeRules = true,
    includeTxGroups = true,
    includeGrounding = true,
  } = params

  const res = await fetch(BASE(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      scout_account_id: scoutAccountId,
      application_name: applicationName,
      user_id: userId,
      ca_job_id: caJobId,
      scope,
      include_spec: includeSpec,
      include_rules: includeRules,
      include_tx_groups: includeTxGroups,
      include_grounding: includeGrounding,
    }),
  })

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(err.detail || `EBL request failed: ${res.status}`)
  }
  return res.json()
}

/**
 * Poll EBL job status.
 *
 * @param {string} jobId
 * @returns {Promise<{ job_id, status, rule_count, program_count, manifest_uri }>}
 */
export async function getEBLStatus(jobId) {
  const res = await fetch(`${BASE()}/${jobId}/status`)
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(err.detail || `EBL status failed: ${res.status}`)
  }
  return res.json()
}

/**
 * Fetch full EBL results (all artifact content) for a completed job.
 *
 * @param {string} jobId
 * @returns {Promise<{
 *   job_id, status, rule_count, program_count,
 *   business_rules_catalog, program_specs, application_overview,
 *   ebl_metrics, artifacts
 * }>}
 */
export async function getEBLResults(jobId) {
  const res = await fetch(`${BASE()}/${jobId}/results`)
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }))
    throw new Error(err.detail || `EBL results failed: ${res.status}`)
  }
  return res.json()
}

/**
 * Poll until EBL job is done or fails.
 * Resolves with the results object on success; rejects on failure or timeout.
 *
 * @param {string} jobId
 * @param {object} [opts]
 * @param {number} [opts.intervalMs]   - polling interval (default 3000ms)
 * @param {number} [opts.timeoutMs]    - max wait (default 120000ms)
 * @param {function} [opts.onProgress] - called with status string each poll
 * @returns {Promise<EBLResultsResponse>}
 */
export async function waitForEBL(jobId, opts = {}) {
  const {
    intervalMs = 3000,
    timeoutMs  = 120_000,
    onProgress = null,
  } = opts

  const deadline = Date.now() + timeoutMs

  while (Date.now() < deadline) {
    const status = await getEBLStatus(jobId)
    const s = (status.status || '').toLowerCase()

    if (onProgress) onProgress(s)

    if (s === 'completed' || s === 'succeeded') {
      return getEBLResults(jobId)
    }
    if (s === 'failed' || s === 'error') {
      throw new Error(`EBL job ${jobId} failed.`)
    }

    await new Promise(r => setTimeout(r, intervalMs))
  }
  throw new Error(`EBL job ${jobId} timed out after ${timeoutMs / 1000}s.`)
}
